/* ============================================================================
   Curated brand-group registry.
   There is NO official konsern/parent feed in the data · each subsidiary is an
   independent farm_operators row, and the split is fuzzy (e.g. "Sjøtroll
   Havbruk" belongs to Lerøy but carries no "Lerøy" in its name; "SalMar
   Settefisk" is hatchery, not sea-phase). So group membership is defined here
   explicitly, keyed by org_nr · the only reliable method.

   Only sea-phase ("matfisk") entities should be listed; hatchery/smolt and
   processing entities are intentionally excluded so biomass/harvest aggregate
   correctly. Org numbers verified against /v1/operators.
   ========================================================================== */

export interface BrandGroup {
  slug: string; // group route key, e.g. "group-mowi"
  name: string; // display name
  /** Member org numbers (sea-phase legal entities). */
  orgNrs: string[];
  aka?: string[]; // extra search aliases
}

export const GROUPS: BrandGroup[] = [
  {
    slug: "group-mowi",
    name: "MOWI",
    orgNrs: ["921668236", "964118191"], // Seawater Norway, MOWI ASA
    aka: ["marine harvest"],
  },
  {
    slug: "group-salmar",
    name: "SalMar",
    // Oppdrett, Farming, AS, Dåfjord (Settefisk excluded · hatchery)
    orgNrs: ["928957489", "966840528", "958973306", "935182484"],
  },
  {
    slug: "group-leroy",
    name: "Lerøy Seafood",
    // Aurora Sjø, Aurora, Midt Sjø, Midt, Sjøtroll Havbruk Sjø
    orgNrs: ["930155179", "985940460", "930155209", "985848718", "930181528"],
    aka: ["leroy", "lerøy", "sjøtroll", "sjotroll", "aurora", "hydrotech"],
  },
  {
    slug: "group-grieg",
    name: "Grieg Seafood",
    orgNrs: ["935496578", "838065392"], // Farming, Rogaland
  },
  {
    slug: "group-cermaq",
    name: "Cermaq Norway",
    orgNrs: ["930152366", "961922976"],
  },
  {
    slug: "group-nordlaks",
    name: "Nordlaks",
    orgNrs: ["929911946", "955750802"], // Havbruk, Oppdrett (Smolt/Produkter excluded)
  },
  {
    slug: "group-novasea",
    name: "Nova Sea",
    orgNrs: ["827248312", "961056268"],
  },
];

const BY_ORG = new Map<string, BrandGroup>();
for (const g of GROUPS) for (const o of g.orgNrs) BY_ORG.set(o, g);

export function groupForOrg(orgNr: string): BrandGroup | undefined {
  return BY_ORG.get(orgNr);
}

export function groupBySlug(slug: string): BrandGroup | undefined {
  return GROUPS.find((g) => g.slug === slug);
}

/** Fuzzy match a query against group names/aliases. */
export function matchGroups(q: string): BrandGroup[] {
  const t = q.trim().toLowerCase();
  if (t.length < 2) return [];
  return GROUPS.filter(
    (g) =>
      g.name.toLowerCase().includes(t) ||
      g.slug.includes(t) ||
      (g.aka ?? []).some((a) => a.includes(t) || t.includes(a)),
  );
}
